import React, { useCallback, useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isMusic24Locale, Music24Locale, MUSIC24_LOCALE_NAMES } from 'fym-shared/src/music24/music24Locales';
import { TypedMemo } from '../../../common/types';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { useAppLocale } from '../../../app/locale/AppIntlProvider';
import { SettingsSaveStatus } from '../SettingsSaveStatus';
import { useSettingsSaveState } from '../useSettingsSaveState';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';

const messages = defineMessages({
  title: { id: 'app.sml.settings.account.language.title', defaultMessage: 'Language' },
  description: {
    id: 'app.sml.settings.account.language.description',
    defaultMessage: 'The language of MUSIC24 and of the emails we send you.',
  },
  automatic: {
    id: 'app.sml.settings.account.language.automatic',
    defaultMessage: 'Automatic (browser language)',
  },
});

/** The option order: each language in its own name, English last. */
const LANGUAGE_OPTIONS: readonly Music24Locale[] = ['de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'sv', 'en'];

/** The `<select>` value of "Automatic". */
const AUTOMATIC = 'auto';

interface Props {
  /** Called after the server saved the language, so the user data is read again. */
  onSaved?: () => void;
}

/**
 * Settings → Account → Language. A pick switches the UI at once (`useAppLocale`), keeps it in the
 * browser for the next first paint and saves it to the user (`users.updateLanguage`). A failed
 * save puts the language back.
 */
const AccountLanguageCardComponent: React.FC<Props> = ({ onSaved }) => {
  const intl = useIntl();
  const trpcClient = useTRPCClientAPI();
  const selectId = useId();
  const { preference, setPreference } = useAppLocale();
  const save = useSettingsSaveState();

  const mutation = useTRPCMutation({
    mutationFn: (locale: Music24Locale | null) => trpcClient.users.updateLanguage.mutate({ locale }),
  });

  const choose = useCallback(
    (value: string) => {
      const next = isMusic24Locale(value) ? value : null;
      if (next === preference) {
        return;
      }
      const previous = preference;
      setPreference(next);
      save.setState('saving');
      mutation.mutate(next, {
        onSuccess: () => {
          save.setState('saved');
          onSaved?.();
        },
        onError: () => {
          setPreference(previous);
          save.setState('failed');
        },
      });
    },
    [mutation, onSaved, preference, save, setPreference]
  );

  return (
    <Panel as="section">
      <PanelHead
        title={
          <label htmlFor={selectId} className="mb-0">
            {intl.formatMessage(messages.title)}
          </label>
        }
        subtitle={intl.formatMessage(messages.description)}
        right={<SettingsSaveStatus state={save.state} />}
      />
      <PanelBody>
        <select
          id={selectId}
          className="form-select"
          value={preference ?? AUTOMATIC}
          disabled={mutation.isPending}
          onChange={(event) => choose(event.target.value)}
        >
          <option value={AUTOMATIC}>{intl.formatMessage(messages.automatic)}</option>
          {LANGUAGE_OPTIONS.map((locale) => (
            <option key={locale} value={locale} lang={locale}>
              {MUSIC24_LOCALE_NAMES[locale]}
            </option>
          ))}
        </select>
      </PanelBody>
    </Panel>
  );
};

export const AccountLanguageCard = TypedMemo(AccountLanguageCardComponent);
